import { useEffect } from 'react';

export interface ShortcutOptions {
  // Si true, ignora el shortcut cuando hay un input/textarea/contenteditable
  // enfocado (útil para `/`, no para Cmd+K).
  readonly skipInputs?: boolean;
}

function isInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

const MODIFIER_KEYS = ['cmd', 'mod', 'shift', 'alt'];

// Combo strings: 'cmd+k', 'cmd+shift+l', '/', 'esc'.
// 'cmd' = metaKey en Mac OR ctrlKey en Windows/Linux (cross-platform).
export function useShortcut(
  combo: string,
  handler: () => void,
  options: ShortcutOptions = {},
): void {
  const skipInputs = options.skipInputs ?? false;

  useEffect(() => {
    const parts = combo
      .toLowerCase()
      .split('+')
      .map((p) => p.trim());
    const needsCmd = parts.includes('cmd') || parts.includes('mod');
    const needsShift = parts.includes('shift');
    const needsAlt = parts.includes('alt');
    const key = parts.find((p) => !MODIFIER_KEYS.includes(p));
    if (!key) return undefined;

    function onKey(event: KeyboardEvent): void {
      const cmdPressed = event.metaKey || event.ctrlKey;
      if (needsCmd !== cmdPressed) return;
      if (needsShift !== event.shiftKey) return;
      if (needsAlt !== event.altKey) return;
      if (event.key.toLowerCase() !== key) return;
      if (skipInputs && isInputTarget(event.target)) return;
      event.preventDefault();
      handler();
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo, handler, skipInputs]);
}
